import { useState, useEffect } from 'react';
import { 
  MapPin, Navigation, Clock, Package, Upload, CheckCircle, 
  AlertCircle, Truck, FileText, X, Camera, ChevronRight 
} from 'lucide-react';
import { 
  Shipment, 
  ShipmentStatus, 
  SHIPMENT_STATUS_CONFIG,
  ShipmentDocument,
  ShipmentStatusUpdate 
} from '../types/shipment';
import { useAuth } from '../contexts/AuthContext';
import { shipmentService } from '../services/shipmentService';
import { notificationService } from '../services/notificationService';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

const STATUS_SEQUENCE: ShipmentStatus[] = [
  'heading-to-loading',
  'arrived-at-loading',
  'waiting-for-loading',
  'loading-in-progress',
  'loading-completed',
  'heading-to-delivery',
  'arrived-at-delivery',
  'waiting-for-unloading',
  'unloading-in-progress',
  'unloading-completed',
  'delivery-completed',
];

const MOBILE_STAGE_TO_STATUS: Record<number, ShipmentStatus> = {
  0: 'heading-to-loading',
  1: 'waiting-for-loading',
  2: 'loading-in-progress',
  3: 'loading-completed',
  4: 'heading-to-delivery',
  5: 'arrived-at-delivery',
  6: 'waiting-for-unloading',
  7: 'unloading-in-progress',
  8: 'unloading-completed',
};

const stageToStatus = (stage?: number | string | null, fallback?: ShipmentStatus): ShipmentStatus => {
  const numericStage = Number(stage);
  if (!Number.isNaN(numericStage) && MOBILE_STAGE_TO_STATUS[numericStage]) {
    return MOBILE_STAGE_TO_STATUS[numericStage];
  }
  return fallback || 'heading-to-loading';
};

const statusToStage = (status: ShipmentStatus) => {
  const index = STATUS_SEQUENCE.indexOf(status);
  return index >= 0 ? index : 0;
};

const statusToBackendStatus = (status: ShipmentStatus): string => {
  if (status === 'delivery-completed') return 'delivered';
  if (status === 'heading-to-loading') return 'assigned';
  return 'in-transit';
};

export function DriverTracking() {
  const { user } = useAuth();
  const [myShipments, setMyShipments] = useState<Shipment[]>([]);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentType, setDocumentType] = useState<'loading' | 'delivery'>('loading');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMyShipments();
  }, [user]);

  useAutoRefresh(() => {
    if (!user) return;
    return loadMyShipments({ silent: true });
  }, { intervalMs: 15000, enabled: Boolean(user) });

  useEffect(() => {
    if (myShipments.length > 0 && !selectedShipment) {
      setSelectedShipment(myShipments[0]);
    }
  }, [myShipments]);

  const mapApiToShipment = (apiData: any): Shipment => {
    const resolvedStatus = stageToStatus(apiData.current_stage, apiData.current_status || apiData.currentStatus);
    const resolvedStage = statusToStage(resolvedStatus);
    const statusHistory: ShipmentStatusUpdate[] = (apiData.status_history || []).map((h: any) => ({
      id: h.id || String(Date.now()),
      shipmentId: apiData.id,
      status: h.status || resolvedStatus,
      timestamp: h.timestamp || h.created_at || new Date().toISOString(),
      notes: h.notes,
      updatedBy: h.updated_by || apiData.assigned_driver_id || '',
      updatedByRole: h.updated_by_role || 'driver',
    }));

    const fallbackHistory = STATUS_SEQUENCE.slice(0, resolvedStage + 1).map((status, index) => ({
      id: `${apiData.id}-${status}`,
      shipmentId: apiData.id,
      status,
      timestamp: index === resolvedStage ? (apiData.updated_at || new Date().toISOString()) : (apiData.created_at || new Date().toISOString()),
      updatedBy: apiData.assigned_driver_id || '',
      updatedByRole: 'driver',
    }));

    const documents: ShipmentDocument[] = (apiData.documents || []).map((d: any) => ({
      id: d.id,
      shipmentId: apiData.id,
      type: d.type || 'loading',
      fileUrl: d.file_url || d.fileUrl || '',
      fileName: d.file_name || d.fileName || 'Document',
      uploadedAt: d.uploaded_at || d.uploadedAt || new Date().toISOString(),
      uploadedBy: d.uploaded_by || d.uploadedBy || '',
      status: d.status || apiData.current_status || 'pending',
    }));

    return {
      id: apiData.id,
      sevkKodu: apiData.shipment_no || apiData.sevkKodu || 'SEV-0000',
      driverName: apiData.driver_name || apiData.driverName || user?.name || 'Sürücü',
      driverId: apiData.assigned_driver_id || apiData.driverId || user?.id || '',
      vehiclePlate: apiData.vehicle_plate || apiData.vehiclePlate || 'XX XXX XX',
      vehicleId: apiData.vehicle_id || apiData.vehicleId || '',
      loadingPoint: apiData.loading_point || apiData.loadingPoint || '',
      loadingAddress: apiData.loading_address || apiData.loadingAddress || '',
      loadingCity: apiData.loading_city || apiData.loadingCity || '',
      loadingWarehouseId: apiData.loading_warehouse_id || apiData.loadingWarehouseId,
      plannedLoadingTime: apiData.planned_loading_time || apiData.plannedLoadingTime,
      deliveryPoint: apiData.delivery_point || apiData.deliveryPoint || '',
      deliveryAddress: apiData.delivery_address || apiData.deliveryAddress || '',
      deliveryCity: apiData.delivery_city || apiData.deliveryCity || '',
      deliveryWarehouseId: apiData.delivery_warehouse_id || apiData.deliveryWarehouseId,
      plannedDeliveryTime: apiData.planned_delivery_time || apiData.plannedDeliveryTime,
      cargoType: apiData.cargo_type || apiData.cargoType || '',
      cargoWeight: Number(apiData.cargo_weight || apiData.cargoWeight || 0),
      cargoVolume: Number(apiData.cargo_volume || apiData.cargoVolume || 0),
      currentStatus: resolvedStatus,
      statusHistory: statusHistory.length > 0 ? statusHistory : fallbackHistory,
      documents,
      rampAssignment: apiData.ramp_assignment || apiData.rampAssignment,
      currentLocation: apiData.current_location || apiData.currentLocation || (
        apiData.current_latitude && apiData.current_longitude
          ? {
              lat: Number(apiData.current_latitude),
              lng: Number(apiData.current_longitude),
              lastUpdated: apiData.current_location_updated_at || new Date().toISOString(),
            }
          : undefined
      ),
      createdAt: apiData.created_at || apiData.createdAt || new Date().toISOString(),
      updatedAt: apiData.updated_at || apiData.updatedAt || new Date().toISOString(),
      completedAt: apiData.completed_at || apiData.completedAt,
    };
  };

  const loadMyShipments = async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const response = await shipmentService.getAll({ driverId: user?.id });
      if (response.success && response.data) {
        const shipments = response.data
          .map(mapApiToShipment)
          .filter(s => s.currentStatus !== 'delivery-completed');
        setMyShipments(shipments);
        if (shipments.length > 0 && !selectedShipment) {
          setSelectedShipment(shipments[0]);
        }
      } else {
        setMyShipments([]);
      }
    } catch (err) {
      console.error('Failed to load shipments:', err);
      setError('Sevkiyatlar yüklenirken bir hata oluştu');
      setMyShipments([]);
    } finally {
      if (!options?.silent) setLoading(false);
    }
  };

  const updateShipmentStatus = async (newStatus: ShipmentStatus, skipDocumentCheck = false) => {
    if (!selectedShipment) return;

    const currentConfig = SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
      label: 'Bilinmiyor',
      color: '#6b7280',
      bgColor: '#f3f4f6',
      description: 'Durum bilinmiyor'
    };
    
    // Evrak kontrolü
    if (currentConfig.requiresDocument && !skipDocumentCheck) {
      const docType = selectedShipment.currentStatus === 'loading-completed' ? 'loading' : 'delivery';
      setDocumentType(docType);
      setShowDocumentModal(true);
      return;
    }

    const statusUpdate = {
      status: newStatus,
      notes: notes || undefined,
      updatedBy: user?.id || 'driver',
      updatedByRole: 'driver',
    };

    try {
      await shipmentService.update(selectedShipment.id, {
        current_stage: statusToStage(newStatus),
        status: statusToBackendStatus(newStatus),
        notes: notes || undefined,
      });

      const refreshed = await shipmentService.getById(selectedShipment.id);
      if (refreshed.success && refreshed.data) {
        const updatedShipment = mapApiToShipment(refreshed.data);

        if (newStatus === 'delivery-completed') {
          updatedShipment.completedAt = new Date().toISOString();
        }

        createNotification(updatedShipment, newStatus);

        setMyShipments(prev => prev.map(s => s.id === selectedShipment.id ? updatedShipment : s));
        setSelectedShipment(updatedShipment);
        setNotes('');
      }
    } catch (err) {
      console.error('Failed to update shipment status:', err);
    }
  };

  const handleDocumentUpload = async () => {
    if (!uploadedFile || !selectedShipment) return;

    const newDocument = {
      type: documentType,
      fileName: uploadedFile.name,
      status: selectedShipment.currentStatus,
    };

    try {
      await shipmentService.update(selectedShipment.id, {
        notes: notes || undefined,
      });

      const refreshed = await shipmentService.getById(selectedShipment.id);
      if (refreshed.success && refreshed.data) {
        const updatedShipment = mapApiToShipment(refreshed.data);
        setSelectedShipment(updatedShipment);
        setMyShipments(prev => prev.map(s => s.id === selectedShipment.id ? updatedShipment : s));
      }

      setShowDocumentModal(false);
      setUploadedFile(null);

      const config = SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
        label: 'Bilinmiyor',
        color: '#6b7280',
        bgColor: '#f3f4f6',
        description: 'Durum bilinmiyor'
      };
      if (config.nextStatus) {
        updateShipmentStatus(config.nextStatus, true);
      }
    } catch (err) {
      console.error('Failed to upload document:', err);
    }
  };

  const createNotification = async (shipment: Shipment, newStatus: ShipmentStatus) => {
    const config = SHIPMENT_STATUS_CONFIG[newStatus] || {
      label: 'Bilinmiyor',
      color: '#6b7280',
      bgColor: '#f3f4f6',
      description: 'Durum bilinmiyor'
    };

    try {
      await notificationService.create({
        type: 'status-update',
        title: `Sevkiyat Güncellendi: ${shipment.sevkKodu}`,
        message: `${config.label} - ${shipment.driverName} (${shipment.vehiclePlate})`,
        shipmentId: shipment.id,
        recipients: ['admin'],
      });
    } catch (err) {
      console.error('Failed to create notification:', err);
    }
  };

  const canProceedToNext = () => {
    if (!selectedShipment) return false;
    const config = SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
      label: 'Bilinmiyor',
      color: '#6b7280',
      bgColor: '#f3f4f6',
      description: 'Durum bilinmiyor'
    };
    return !!config.nextStatus;
  };

  const getNextStatusLabel = () => {
    if (!selectedShipment) return '';
    const config = SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
      label: 'Bilinmiyor',
      color: '#6b7280',
      bgColor: '#f3f4f6',
      description: 'Durum bilinmiyor'
    };
    if (!config.nextStatus) return '';
    return (SHIPMENT_STATUS_CONFIG[config.nextStatus] || { label: 'Sonraki Adım' }).label;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Sevkiyatlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Hata</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={loadMyShipments}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  if (!selectedShipment || myShipments.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Aktif Sevkiyat Yok</h2>
          <p className="text-gray-600">Şu anda size atanmış aktif sevkiyat bulunmuyor.</p>
        </div>
      </div>
    );
  }

  const currentConfig = SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus];
  const progressPercentage = (selectedShipment.statusHistory.length / 11) * 100;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sevkiyat Takip</h1>
          <p className="text-gray-600 mt-1">Sevkiyatınızı adım adım güncelleyin</p>
        </div>
      </div>

      {/* Active Shipment Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{selectedShipment.sevkKodu}</h2>
            <p className="text-sm text-gray-600 mt-1">{selectedShipment.vehiclePlate}</p>
          </div>
          <div 
            className="px-4 py-2 rounded-lg font-semibold"
            style={{ backgroundColor: currentConfig.bgColor, color: currentConfig.color }}
          >
            {currentConfig.label}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">İlerleme</span>
            <span className="font-semibold text-gray-900">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Route Info */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">Yükleme Noktası</p>
                <p className="text-lg font-bold text-blue-900 mt-1">{selectedShipment.loadingPoint}</p>
                <p className="text-sm text-blue-700 mt-1">{selectedShipment.loadingAddress}</p>
                {selectedShipment.plannedLoadingTime && (
                  <div className="flex items-center gap-1 mt-2 text-sm text-blue-700">
                    <Clock className="w-4 h-4" />
                    {new Date(selectedShipment.plannedLoadingTime).toLocaleString('tr-TR')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">Teslimat Noktası</p>
                <p className="text-lg font-bold text-green-900 mt-1">{selectedShipment.deliveryPoint}</p>
                <p className="text-sm text-green-700 mt-1">{selectedShipment.deliveryAddress}</p>
                {selectedShipment.plannedDeliveryTime && (
                  <div className="flex items-center gap-1 mt-2 text-sm text-green-700">
                    <Clock className="w-4 h-4" />
                    {new Date(selectedShipment.plannedDeliveryTime).toLocaleString('tr-TR')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Cargo Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Yük Bilgileri</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Yük Tipi</p>
              <p className="font-semibold text-gray-900 mt-1">{selectedShipment.cargoType}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Ağırlık</p>
              <p className="font-semibold text-gray-900 mt-1">{selectedShipment.cargoWeight.toLocaleString()} kg</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Hacim</p>
              <p className="font-semibold text-gray-900 mt-1">{selectedShipment.cargoVolume} m³</p>
            </div>
          </div>
        </div>

        {/* Current Status Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-900">{currentConfig.label}</p>
              <p className="text-sm text-yellow-700 mt-1">{currentConfig.description}</p>
              {currentConfig.requiresDocument && (
                <p className="text-sm text-yellow-800 font-medium mt-2">
                  ⚠️ Bu aşamadan devam edebilmek için evrak yüklemeniz gerekmektedir.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Rampa Assignment */}
        {selectedShipment.rampAssignment && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              <div>
                <p className="font-medium text-purple-900">Rampa Atandı</p>
                <p className="text-sm text-purple-700 mt-1">
                  Lütfen <span className="font-bold">{selectedShipment.rampAssignment.rampName}</span> rampasına yanaşın
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notes Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Not Ekle (Opsiyonel)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={2}
            placeholder="Bu aşama ile ilgili not ekleyebilirsiniz..."
          />
        </div>

        {/* Action Button */}
        {canProceedToNext() && (
          <button
            onClick={() => {
              const config = SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus];
              if (config.nextStatus) {
                updateShipmentStatus(config.nextStatus);
              }
            }}
            className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            {getNextStatusLabel()}
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {!canProceedToNext() && (
          <div className="bg-green-100 border border-green-300 rounded-lg p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="font-semibold text-green-900">Sevkiyat Tamamlandı!</p>
            <p className="text-sm text-green-700 mt-1">Bu sevkiyat başarıyla teslim edilmiştir.</p>
          </div>
        )}
      </div>

      {/* Status History */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Durum Geçmişi</h3>
        <div className="space-y-4">
          {selectedShipment.statusHistory.map((update, index) => {
            const config = SHIPMENT_STATUS_CONFIG[update.status];
            return (
              <div key={update.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: config.bgColor }}
                  >
                    <CheckCircle className="w-5 h-5" style={{ color: config.color }} />
                  </div>
                  {index < selectedShipment.statusHistory.length - 1 && (
                    <div className="w-0.5 h-full bg-gray-200 my-1" />
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-gray-900">{config.label}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(update.timestamp).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  {update.notes && (
                    <p className="text-sm text-gray-600 mt-1">{update.notes}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Uploaded Documents */}
      {selectedShipment.documents.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Yüklenen Evraklar</h3>
          <div className="space-y-3">
            {selectedShipment.documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">{doc.fileName}</p>
                    <p className="text-sm text-gray-600">
                      {doc.type === 'loading' ? 'Yükleme Evrakı' : 'Teslimat Evrakı'} - 
                      {new Date(doc.uploadedAt).toLocaleString('tr-TR')}
                    </p>
                  </div>
                </div>
                <a 
                  href={doc.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Görüntüle
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {documentType === 'loading' ? 'Yükleme Evrakı' : 'Teslimat Evrakı'} Yükle
              </h3>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-4">
                  Kaşeli ve imzalı {documentType === 'loading' ? 'yükleme' : 'teslimat'} evrakını yükleyin
                </p>
                <label className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                  {uploadedFile ? 'Dosya Değiştir' : 'Dosya Seç'}
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        setUploadedFile(e.target.files[0]);
                      }
                    }}
                  />
                </label>
                {uploadedFile && (
                  <p className="text-sm text-green-600 mt-3 font-medium">
                    ✓ {uploadedFile.name}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDocumentModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={handleDocumentUpload}
                disabled={!uploadedFile}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Yükle ve Devam Et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
